import { render } from 'lit';

type PreviewContent = Parameters<typeof render>[0];

interface PreviewFrameState {
  content: PreviewContent;
  initializedDocument: Document | null;
  loadListenerInstalled: boolean;
  referenceViewportHeight: number;
  renderKey: string;
  renderedKey: string | null;
  resizeObserver: ResizeObserver | null;
  resizeFrame: number | null;
}

const previewFrameStates = new WeakMap<HTMLIFrameElement, PreviewFrameState>();

type CanvasPanRoot = HTMLElement | Document;

interface CanvasPanSession {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startScrollLeft: number;
  startScrollTop: number;
  scale: number;
  sourceDocument: Document;
  cleanup: () => void;
}

interface CanvasPanState {
  sessions: Set<CanvasPanSession>;
  sourceCleanups: Set<() => void>;
  frameLoadListeners: Map<HTMLIFrameElement, () => void>;
  sourceDocuments: Set<Document>;
  observer: MutationObserver | null;
}

const canvasPanStates = new WeakMap<HTMLElement, CanvasPanState>();

const isHandToolActive = (viewport: HTMLElement) =>
  viewport.closest<HTMLElement>('.vp-editor-app')?.classList.contains('is-hand-tool') ?? false;

const addCanvasPanSource = (
  viewport: HTMLElement,
  root: CanvasPanRoot,
  sourceDocument: Document,
  scaleForEvent: () => number,
  state: CanvasPanState,
) => {
  const onPointerDown = (event: PointerEvent) => {
    const isMiddleButton = event.button === 1;
    const isHandDrag = event.button === 0 && isHandToolActive(viewport);
    if ((!isMiddleButton && !isHandDrag) || !event.isPrimary) return;

    event.preventDefault();
    event.stopPropagation();
    state.sessions.forEach(session => {
      session.cleanup();
    });

    const target = event.target as Element | null;
    if (target && typeof target.setPointerCapture === 'function') {
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        // A pointer may already have left a re-rendered preview document.
      }
    }

    const move = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      moveEvent.preventDefault();
      const scale = Math.max(0.01, scaleForEvent());
      viewport.scrollLeft = Math.max(0, session.startScrollLeft - (moveEvent.clientX - session.startClientX) * scale);
      viewport.scrollTop = Math.max(0, session.startScrollTop - (moveEvent.clientY - session.startClientY) * scale);
      viewport.classList.add('is-canvas-panning');
    };
    const stop = (stopEvent: PointerEvent) => {
      if (stopEvent.pointerId !== event.pointerId) return;
      session.cleanup();
    };
    const session: CanvasPanSession = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollLeft: viewport.scrollLeft,
      startScrollTop: viewport.scrollTop,
      scale: scaleForEvent(),
      sourceDocument,
      cleanup: () => {
        sourceDocument.removeEventListener('pointermove', move, true);
        sourceDocument.removeEventListener('pointerup', stop, true);
        sourceDocument.removeEventListener('pointercancel', stop, true);
        state.sessions.delete(session);
        viewport.classList.remove('is-canvas-panning');
      },
    };
    state.sessions.add(session);
    sourceDocument.addEventListener('pointermove', move, true);
    sourceDocument.addEventListener('pointerup', stop, true);
    sourceDocument.addEventListener('pointercancel', stop, true);
  };

  const pointerDownListener: EventListener = event => onPointerDown(event as PointerEvent);
  root.addEventListener('pointerdown', pointerDownListener, true);
  const cleanup = () => root.removeEventListener('pointerdown', pointerDownListener, true);
  state.sourceCleanups.add(cleanup);
  return cleanup;
};

/**
 * Enables middle-button canvas panning for the legacy editor, including inside same-origin preview
 * iframes. Holding the hand tool also enables left-button panning; ordinary selection stays intact.
 */
export const installEditorCanvasPanning = (viewport: HTMLElement): (() => void) => {
  const existing = canvasPanStates.get(viewport);
  if (existing) return () => undefined;

  const state: CanvasPanState = {
    sessions: new Set(),
    sourceCleanups: new Set(),
    frameLoadListeners: new Map(),
    sourceDocuments: new Set(),
    observer: null,
  };
  canvasPanStates.set(viewport, state);
  const ownerDocument = viewport.ownerDocument;
  addCanvasPanSource(viewport, viewport, ownerDocument, () => 1, state);

  const bindFrame = (frame: HTMLIFrameElement) => {
    const bindDocument = () => {
      const frameDocument = frame.contentDocument;
      if (!frameDocument || state.sourceDocuments.has(frameDocument)) return;
      const frameRectScale = () => {
        const frameRect = frame.getBoundingClientRect();
        return frame.offsetWidth ? frameRect.width / frame.offsetWidth : 1;
      };
      state.sourceDocuments.add(frameDocument);
      addCanvasPanSource(viewport, frameDocument, frameDocument, frameRectScale, state);
    };
    if (!state.frameLoadListeners.has(frame)) {
      state.frameLoadListeners.set(frame, bindDocument);
      frame.addEventListener('load', bindDocument);
    }
    bindDocument();
  };

  const bindFrames = () => viewport.querySelectorAll<HTMLIFrameElement>('iframe.vp-editor-frame').forEach(bindFrame);
  bindFrames();
  const MutationObserverConstructor = ownerDocument.defaultView?.MutationObserver;
  if (MutationObserverConstructor) {
    state.observer = new MutationObserverConstructor(bindFrames);
    state.observer.observe(viewport, { childList: true, subtree: true });
  }

  return () => {
    state.sessions.forEach(session => {
      session.cleanup();
    });
    state.sourceCleanups.forEach(cleanup => {
      cleanup();
    });
    state.frameLoadListeners.forEach((listener, frame) => {
      frame.removeEventListener('load', listener);
    });
    state.observer?.disconnect();
    canvasPanStates.delete(viewport);
  };
};

const freezeViewportHeightUnits = (css: string) =>
  css.replace(/(-?(?:\d+\.?\d*|\.\d+))(dvh|svh|lvh|vh)\b/gi, 'calc(var(--vp-preview-viewport-height) * $1 / 100)');

const clonePreviewStylesheet = (node: HTMLElement, targetDocument: Document) => {
  const stylesheetNode = node as HTMLStyleElement | HTMLLinkElement;
  try {
    if (stylesheetNode.sheet) {
      const style = targetDocument.createElement('style');
      style.textContent = freezeViewportHeightUnits(
        [...stylesheetNode.sheet.cssRules].map(rule => rule.cssText).join('\n'),
      );
      return style;
    }
  } catch {
    // Cross-origin stylesheets cannot be read through CSSOM. Cloning their link still preserves them.
  }
  return node.cloneNode(true);
};

const copyPreviewStyles = (targetDocument: Document) => {
  const base = targetDocument.createElement('base');
  base.href = document.baseURI;

  const viewport = targetDocument.createElement('meta');
  viewport.name = 'viewport';
  viewport.content = 'width=device-width, initial-scale=1';

  const frameFoundation = targetDocument.createElement('style');
  frameFoundation.textContent = `
    html,
    body {
      width: 100%;
      min-height: var(--vp-preview-viewport-height);
      margin: 0;
      background: var(--vp-paper, #fff);
    }

    .vp-editor-preview-event-root {
      min-height: var(--vp-preview-viewport-height);
    }

    .vp-editor-preview-page {
      height: auto !important;
      min-height: var(--vp-preview-viewport-height) !important;
      overflow: visible !important;
      scrollbar-width: none;
    }

    .vp-editor-preview-page::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  `;

  const styles = [...document.head.querySelectorAll<HTMLElement>('style, link[rel="stylesheet"]')].map(node =>
    clonePreviewStylesheet(node, targetDocument),
  );
  targetDocument.head.replaceChildren(base, viewport, ...styles, frameFoundation);
};

const fitFrameToFullPage = (frame: HTMLIFrameElement, state: PreviewFrameState) => {
  if (state.resizeFrame !== null) window.cancelAnimationFrame(state.resizeFrame);
  state.resizeFrame = window.requestAnimationFrame(() => {
    state.resizeFrame = null;
    const page = frame.contentDocument?.querySelector<HTMLElement>('.vp-editor-preview-page');
    if (!page) return;

    const fullPageHeight = Math.max(state.referenceViewportHeight, page.scrollHeight, page.offsetHeight);
    frame.style.height = `${Math.ceil(fullPageHeight)}px`;
  });
};

const observeFullPageHeight = (frame: HTMLIFrameElement, state: PreviewFrameState) => {
  const targetDocument = frame.contentDocument;
  const page = targetDocument?.querySelector<HTMLElement>('.vp-editor-preview-page');
  if (!targetDocument || !page) return;

  state.resizeObserver?.disconnect();
  const ResizeObserverConstructor = targetDocument.defaultView?.ResizeObserver;
  state.resizeObserver = ResizeObserverConstructor
    ? new ResizeObserverConstructor(() => fitFrameToFullPage(frame, state))
    : null;
  state.resizeObserver?.observe(page);
  fitFrameToFullPage(frame, state);
};

const renderPreviewFrame = (frame: HTMLIFrameElement, state: PreviewFrameState) => {
  const targetDocument = frame.contentDocument;
  if (!targetDocument) return;

  if (state.initializedDocument !== targetDocument) {
    targetDocument.documentElement.lang = document.documentElement.lang || 'de';
    copyPreviewStyles(targetDocument);
    targetDocument.body.className = 'vp-editor-preview-document';
    state.initializedDocument = targetDocument;
    state.renderedKey = null;
  }

  targetDocument.documentElement.style.setProperty(
    '--vp-preview-viewport-height',
    `${state.referenceViewportHeight}px`,
  );
  render(state.content, targetDocument.body);
  state.renderedKey = state.renderKey;
  observeFullPageHeight(frame, state);
};

/**
 * Renders the editable page into a real, isolated browser viewport. Media queries are evaluated
 * against the iframe width, so the 390 px preview follows exactly the same responsive CSS as the
 * published page instead of relying on a second set of editor-only mobile overrides.
 */
export const mountEditorPreviewViewport = (
  frame: HTMLIFrameElement,
  content: PreviewContent,
  referenceViewportHeight: number,
  renderKey: string,
) => {
  const state = previewFrameStates.get(frame) ?? {
    content,
    initializedDocument: null,
    loadListenerInstalled: false,
    referenceViewportHeight,
    renderKey,
    renderedKey: null,
    resizeObserver: null,
    resizeFrame: null,
  };
  state.content = content;
  state.referenceViewportHeight = referenceViewportHeight;
  state.renderKey = renderKey;
  previewFrameStates.set(frame, state);
  frame.style.height ||= `${referenceViewportHeight}px`;
  frame.setAttribute('scrolling', 'no');

  if (!state.loadListenerInstalled) {
    frame.addEventListener('load', () => renderPreviewFrame(frame, state));
    state.loadListenerInstalled = true;
  }

  // Editor chrome (caret, toolbar coordinates, history status) updates often. Only content-affecting
  // state is part of renderKey, so those updates never rebuild all five complete landing pages.
  if (state.initializedDocument !== frame.contentDocument || state.renderedKey !== state.renderKey) {
    renderPreviewFrame(frame, state);
  }
};

export const editorPreviewFrames = () =>
  [...document.querySelectorAll<HTMLIFrameElement>('iframe.vp-editor-frame')].filter(frame => frame.contentDocument);

export const activeEditorPreviewPage = () =>
  document
    .querySelector<HTMLIFrameElement>('iframe.vp-editor-frame.is-active')
    ?.contentDocument?.querySelector<HTMLElement>('.vp-editor-preview-page') ?? null;

export const allEditorPreviewPages = () =>
  editorPreviewFrames()
    .map(frame => frame.contentDocument?.querySelector<HTMLElement>('.vp-editor-preview-page') ?? null)
    .filter((page): page is HTMLElement => Boolean(page));

export const queryEditorPreviewNode = (selector: string, preferActive = true) => {
  if (preferActive) {
    const activeNode = document
      .querySelector<HTMLIFrameElement>('iframe.vp-editor-frame.is-active')
      ?.contentDocument?.querySelector<HTMLElement>(selector);
    if (activeNode) return activeNode;
  }

  for (const frame of editorPreviewFrames()) {
    const node = frame.contentDocument?.querySelector<HTMLElement>(selector);
    if (node) return node;
  }
  return null;
};

/** Converts a node rect from its iframe viewport into the editor window's coordinate system. */
export const editorViewportRect = (node: HTMLElement) => {
  const nodeRect = node.getBoundingClientRect();
  const frame = node.ownerDocument.defaultView?.frameElement;
  if (!(frame instanceof HTMLIFrameElement)) return nodeRect;

  const frameRect = frame.getBoundingClientRect();
  const scale = frame.offsetWidth ? frameRect.width / frame.offsetWidth : 1;
  return new DOMRect(
    frameRect.left + frame.clientLeft * scale + nodeRect.left * scale,
    frameRect.top + frame.clientTop * scale + nodeRect.top * scale,
    nodeRect.width * scale,
    nodeRect.height * scale,
  );
};
